import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { AbstractLeadFormFields, LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { formatText } from "@ui/format";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import { logError } from "@utils/sentry-error";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import React from "react";
import { HIGHEST_LEVEL_OF_EDUCATION_OPTIONS } from "../LeadForm/data";
import type { PensionGapLeadCalcRequest, PensionGapLeadCalcResponse } from "../LeadForm/types";
import * as Step from "./Steps";
import type { StepFields as LeadFormStepFields } from "./Steps/LeadForm";
import type { StepFields as Step1Fields } from "./Steps/Step1";
import { calculate } from "./domain";
import "./style.scss";
import PensionGapLeadForm from "../LeadForm/PensionGapLeadForm";

export interface FormFields extends AbstractLeadFormFields, Step1Fields, LeadFormStepFields {}

interface Props extends AbstractLeadFormProps<PensionGapLeadCalcRequest, PensionGapLeadCalcResponse> {}

interface State extends AbstractLeadFormState<FormFields> {
	calculatorResponse: PensionGapLeadCalcResponse;
	leadId: number;
}

/**
 * @Shortcode
 *
 * @title "Állami nyugdíj kalkulátor"
 * @shortcode "pension_gap_small"
 * @slug "nyugdij-kalkulator"
 * @type "calculator"
 */
class PensionGapSmallCalculator extends PensionGapLeadForm<
	PensionGapLeadCalcRequest,
	PensionGapLeadCalcResponse,
	FormFields,
	Props,
	State
> {
	protected getProductType(): ProductType {
		return "SAVINGS_INSURANCE";
	}
	protected readonly componentType: string = "small-calculator";
	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: `step-1`,
			calculatorResponse: { pensionAmount: 0, pensionGap: 0 },
		};
	}

	protected renderSteps(): ReactNode {
		const {
			submitState,
			leadId,
			calculatorResponse: { pensionAmount, pensionGap },
			values: { email },
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="result" submitState={submitState} bottomLogo />
				<Step.Result
					stepName="result"
					defaultTarget="lead-form"
					submitState={submitState}
					pensionAmount={pensionAmount}
					pensionGap={pensionGap}
					colorVersion="black"
					bottomLogo
					canStepBack
				/>

				<Step.LeadForm
					stepName="lead-form"
					submitState={submitState}
					defaultTarget="success"
					colorVersion="black"
					bottomLogo
					email={email}
					leadId={leadId}
					data={this.getLeadData()}
					canStepBack
				/>

				<Step.SuccessPage
					stepName="success"
					subscriptionTypes={["RETAIL_NEWSLETTER"]}
					title="Köszönjük, hogy leadtad érdeklődésed!"
					successMessage="Hamarosan elküldjük neked a kalkulációd eredményeit"
					colorVersion="black"
					bottomLogo
					userEmail={email}
					subscriptionProductTypes={[this.getProductType()]}
				/>
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "result":
				this.onResult(target);
				break;
			case "success":
				this.sendLead()
					.then((response) => {
						this.setState({ leadId: response.leadId });
						super.onStep(target, false);
					})
					.catch((error: Error) => {
						// TODO: Hibakezelés
						logError(error);
					});
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	private onResult(target: string): void {
		const { values } = this.state;

		this.setState({ submitState: "loading" });

		this.promisePool
			.add(calculate(values))
			.then((response: PensionGapLeadCalcResponse) => {
				if (!response.pensionAmount) {
					this.setState({ submitState: "error" });

					this.promisePool.addTimeout().then(() => {
						this.setState({ submitState: undefined });
					});

					return;
				}

				this.setState(
					{
						calculatorResponse: response,
						submitState: undefined,
					},
					() => {
						super.onStep(target, true);
					},
				);

				this.sendCalculationEvent();
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });
				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
	}

	protected getLeadData(): LeadRequest<PensionGapLeadCalcRequest, PensionGapLeadCalcResponse> {
		const {
			values: { age, income, highestLevelOfEducation, needConsultantCall },
			calculatorResponse: { pensionAmount, pensionGap },
		} = this.state;

		const data = super.getLeadData();
		const otherData = new JsonDataBuilder(data.request.otherDataJson);
		if (needConsultantCall !== undefined && needConsultantCall !== null) {
			otherData.addItem("Szeretné, hogy tanácsadó megkeresse", "request_consultant_call", needConsultantCall);
		}
		otherData.addItem("Várható nyugdíj", "expectedPensionAmount", formatText(pensionAmount, "ft"));
		otherData.addItem("Nyugdíjhiány", "pensionGap", formatText(pensionGap, "ft"));
		otherData.addItem("Életkor", "age", age);
		otherData.addItem(
			"Legmagasabb iskolai végzettség",
			"highestLevelOfEducation",
			HIGHEST_LEVEL_OF_EDUCATION_OPTIONS.find((opt) => opt.value === highestLevelOfEducation)?.label,
		);

		data.request = {
			...data.request,
			otherDataJson: otherData.getJson(),
			income,
			birthYear: dayjs().year() - age,
			preferredProduct: "PENSION",
		};

		return data;
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { age, income, highestLevelOfEducation },
			calculatorResponse: { pensionAmount, pensionGap },
		} = this.state;

		return {
			...super.getAutoresponderFields(),
			pensionAmount: formatText(pensionAmount, "ft"),
			pensionGap: formatText(pensionGap, "ft"),
			age: formatText(age, "év"),
			income: formatText(income, "ft"),
			highestLevelOfEducation: HIGHEST_LEVEL_OF_EDUCATION_OPTIONS.find(
				(opt) => opt.value === highestLevelOfEducation,
			)?.label,
		};
	}
	protected getComponentName(): string {
		return "PensionGapSmallCalculator";
	}
}

export default PensionGapSmallCalculator;
